import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { runTransaction, setDoc } from '../lib/firestore'
import { useFirestoreDoc } from './useFirestore'
import { log } from '../lib/logger'
import type { CrewActivitySeen, CrewSeen, RequestsSeen, RoutineRunsSeen, ScheduleSeen, WhatsNewSeen } from '../types'

type SeenDoc = Partial<RequestsSeen & RoutineRunsSeen & CrewSeen & CrewActivitySeen & WhatsNewSeen & ScheduleSeen>

interface Options {
  /** Owner of the watermark; null disables the hook (unread is 0). */
  uid: string | null
  /** `users/{uid}/private/{docName}`. */
  docName: 'requestsSeen' | 'routineRunsSeen' | 'crewSeen' | 'crewActivitySeen' | 'whatsNewSeen' | 'scheduleSeen'
  /** Per-key watermark at `events.{key}` instead of a single `lastSeen`. */
  key?: string
  /** Epoch millis of every item that can count as unread. */
  times: readonly number[]
  /** The surface is open: advance the watermark and report nothing unread. */
  active: boolean
  /** The items behind `times` are still loading. */
  itemsLoading?: boolean
  /**
   * Watermark to create when none exists yet, instead of counting every item as
   * unread. Written once per mount in a server-checked transaction, so a cached
   * "no doc" snapshot can't clobber a real watermark. Single-`lastSeen` docs only.
   */
  baseline?: number
  logTag: string
}

/**
 * The "seen up to" watermark behind an unread badge: counts `times` newer than
 * the stored watermark, and advances it while the surface is `active`. The
 * watermark lives in a private user doc, so Firestore's persistent cache makes
 * it offline-safe without a device-local copy.
 *
 * `settled` is false while the watermark or items are still resolving (badging
 * then would flash the full count and correct itself), while active, or with
 * no uid — `unread` is 0 in all those cases.
 */
export function useSeenWatermark({
  uid,
  docName,
  key,
  times,
  active,
  itemsLoading = false,
  baseline,
  logTag,
}: Options) {
  const ref = useMemo(
    () => (uid ? (doc(db, 'users', uid, 'private', docName) as DocumentReference<SeenDoc>) : null),
    [uid, docName],
  )
  const { data, loading: seenLoading } = useFirestoreDoc<SeenDoc>(ref)
  const loading = itemsLoading || seenLoading
  // What the baseline transaction left on the server. Transaction writes skip
  // the local cache, and the listener doesn't reliably echo them back.
  const [created, setCreated] = useState<number | null>(null)
  // null = never opened the surface, so every item counts as new.
  const lastSeen = (key === undefined ? (data?.lastSeen ?? created) : data?.events?.[key]) ?? null
  const newestMillis = useMemo(() => Math.max(0, ...times), [times])

  useEffect(() => {
    if (!ref || !active || loading) return
    // With a baseline, null means "not resolved yet" (a cold cache reports the
    // doc missing before the server answers) — stamping now would erase what
    // was unread before the baseline transaction reads the real watermark.
    if (lastSeen === null && baseline !== undefined) return
    if (lastSeen !== null && newestMillis <= lastSeen) return
    // Clock skew between the device and Firestore's serverTimestamp can put a
    // fresh item ahead of `now`, so take whichever is later.
    const stamp = Math.max(newestMillis, Date.now())
    const update = key === undefined ? { lastSeen: stamp } : { events: { [key]: stamp } }
    void setDoc(ref, update, { merge: true }).catch(() => {
      // The badge just reappears next visit — not worth surfacing to the user.
      log(logTag, `failed to write ${docName} watermark`)
    })
  }, [ref, active, loading, key, newestMillis, lastSeen, baseline, docName, logTag])

  // One-shot alternative to `active`: mark seen exactly up to `stamp` (e.g. the
  // newest item's server time), so items arriving afterwards still count.
  const advanceTo = useCallback(
    (stamp: number) => {
      if (!ref || (lastSeen !== null && stamp <= lastSeen)) return
      const update = key === undefined ? { lastSeen: stamp } : { events: { [key]: stamp } }
      void setDoc(ref, update, { merge: true }).catch(() => {
        log(logTag, `failed to write ${docName} watermark`)
      })
    },
    [ref, lastSeen, key, docName, logTag],
  )

  const baselineTried = useRef(false)
  useEffect(() => {
    if (!ref || baseline === undefined || seenLoading || lastSeen !== null || baselineTried.current)
      return
    baselineTried.current = true
    runTransaction(db, ref.path, async tx => {
      const existing = (await tx.get(ref)).data()?.lastSeen
      if (typeof existing === 'number') return existing
      tx.set(ref, { lastSeen: baseline }, { merge: true })
      return baseline
    })
      .then(setCreated)
      .catch(() => {
        // Offline or denied — retried on the next mount; until then nothing shows.
        log(logTag, `failed to create ${docName} baseline`)
        baselineTried.current = false
      })
  }, [ref, baseline, seenLoading, lastSeen, docName, logTag])

  return useMemo(() => {
    const settled = ref !== null && !active && !loading
    if (!settled) return { unread: 0, settled, lastSeen, loading, advanceTo }
    const since = lastSeen ?? 0
    return { unread: times.filter(t => t > since).length, settled, lastSeen, loading, advanceTo }
  }, [ref, active, loading, lastSeen, times, advanceTo])
}
